import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Card,
	Dropdown,
	Empty,
	Input,
	List,
	Modal,
	Popconfirm,
	Select,
	Space,
	Spin,
	Tag,
	Typography,
	notification,
} from 'antd';
import type { MenuProps } from 'antd';
import {
	ClearOutlined,
	ColumnWidthOutlined,
	DeleteOutlined,
	EditOutlined,
	EnvironmentOutlined,
	MoreOutlined,
	PlusOutlined,
	SearchOutlined,
	SyncOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import BasePageLayout from '../layout/BasePageLayout';
import useIsShopManager from '../../../hooks/useIsShopManager';
import { useGetBuildingsQuery } from '../../../redux/api/location';
import {
	ILocateMaterialResult,
	ILocationDiagram,
	IRackListItem,
	IShelfWithLocations,
	useDeleteRackMutation,
	useDeleteShelfMutation,
	useGetRackQuery,
	useGetRacksQuery,
	useLazyLocateMaterialQuery,
	useRecalculateRackValuesMutation,
} from '../../../redux/api/inventory';
import { STORAGE_TYPE_LABELS, STORAGE_TYPES } from '../constants/inventory';
import RackVisual from '../components/inventory/RackVisual';
import RackNavBar from '../components/inventory/RackNavBar';
import RackFormModal from '../components/inventory/RackFormModal';
import ShelfFormModal from '../components/inventory/ShelfFormModal';
import LocationFormModal from '../components/inventory/LocationFormModal';
import LocationDetailModal from '../components/inventory/LocationDetailModal';

const { Text } = Typography;

const ManageInventoryPage: React.FC = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const { data: buildings = [] } = useGetBuildingsQuery();
	const { isShopManager } = useIsShopManager();

	const showPermissionDenied = () =>
		notification.warning({
			message: 'Permission Denied',
			description: 'You do not have permission to perform this action.',
		});

	const requireShopManager = (action: () => void) => () => {
		if (isShopManager === false) {
			showPermissionDenied();
			return;
		}
		action();
	};

	// Collapse the rack action toolbar into an overflow dropdown when the inline
	// button group no longer fits, so the destructive Delete Rack control is
	// never pushed off-screen (PTB-5926). The collapse is driven by the *panel*
	// width (measured via ResizeObserver) rather than a viewport breakpoint: the
	// rack list keeps the detail panel narrower than the window, so a viewport
	// breakpoint leaves a band (~992–1200px) where the inline buttons still
	// overflow. Below this content width the actions collapse into the menu.
	const RACK_TOOLBAR_COLLAPSE_WIDTH = 750;
	const [collapseActions, setCollapseActions] = useState(false);
	const toolbarObserverRef = useRef<ResizeObserver | null>(null);

	// Callback ref so the observer attaches/detaches as the toolbar mounts and
	// unmounts with the selected rack (the detail panel renders conditionally).
	const toolbarRef = useCallback((node: HTMLDivElement | null) => {
		toolbarObserverRef.current?.disconnect();
		if (!node || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect.width ?? node.clientWidth;
			setCollapseActions(width < RACK_TOOLBAR_COLLAPSE_WIDTH);
		});
		observer.observe(node);
		toolbarObserverRef.current = observer;
	}, []);

	useEffect(() => () => toolbarObserverRef.current?.disconnect(), []);

	const buildingId = searchParams.get('building') || buildings[0]?.id;

	const { data: racks = [], isLoading: racksLoading } = useGetRacksQuery(
		buildingId ? { building: buildingId } : undefined,
		{ skip: !buildingId },
	);

	// Selected rack is driven by the URL (?rack=<code>) so it survives reloads
	// and can be shared. Falls back to the first rack when nothing is selected.
	const rackParam = searchParams.get('rack') || undefined;
	const locationParam = searchParams.get('location') || undefined;
	const selectedRack = useMemo(
		() => racks.find((r) => r.rack_code === rackParam) || racks[0],
		[racks, rackParam],
	);
	const selectedRackId = selectedRack?.id;

	const selectRack = (code?: string) => {
		const params = new URLSearchParams(searchParams);
		if (code) params.set('rack', code);
		else params.delete('rack');
		setSearchParams(params);
	};

	// Reconcile the URL rack code once the (new building's) racks have loaded:
	// if the selected code no longer matches any rack, drop the stale param so
	// the URL stays coherent with the racks[0] fallback. See PTB-5921.
	useEffect(() => {
		if (!racks.length || !rackParam) return;
		if (racks.some((r) => r.rack_code === rackParam)) return;
		const params = new URLSearchParams(searchParams);
		params.delete('rack');
		setSearchParams(params, { replace: true });
	}, [racks, rackParam, searchParams, setSearchParams]);

	// ─── Rack list search / filter ───────────────────────────────
	const [rackQuery, setRackQuery] = useState('');
	const [typeFilter, setTypeFilter] = useState<string | undefined>();

	const filteredRacks = useMemo(() => {
		const q = rackQuery.trim().toLowerCase();
		return racks.filter((r) => {
			if (typeFilter && r.storage_type !== typeFilter) return false;
			if (!q) return true;
			return (
				r.rack_code.toLowerCase().includes(q) ||
				r.rack_name.toLowerCase().includes(q)
			);
		});
	}, [racks, rackQuery, typeFilter]);

	const typeOptions = useMemo(() => {
		const present = new Set(racks.map((r) => r.storage_type));
		return STORAGE_TYPES.filter((t) => present.has(t.value));
	}, [racks]);

	const filtersActive = !!rackQuery.trim() || !!typeFilter;
	const resetFilters = () => {
		setRackQuery('');
		setTypeFilter(undefined);
	};

	// Keep the detail panel coherent with the filtered list: when the active
	// filters hide the currently selected rack, the panel must not keep showing
	// it (with its action buttons). See PTB-5920.
	const isSelectedVisible =
		!filtersActive ||
		(!!selectedRackId && filteredRacks.some((r) => r.id === selectedRackId));

	const { data: rackDetail, isFetching: rackLoading } = useGetRackQuery(
		selectedRackId as string,
		{ skip: !selectedRackId },
	);

	// Slot highlighted by the rack nav-bar search; reset when the rack changes.
	const [highlightCode, setHighlightCode] = useState<string | undefined>();
	useEffect(() => {
		setHighlightCode(undefined);
	}, [selectedRackId]);

	// Apply highlight from ?location= URL param once the rack detail has loaded.
	// This allows deep-linking directly to a specific slot from external pages.
	useEffect(() => {
		if (!locationParam || !rackDetail) return;
		if (rackDetail.rack_code !== (rackParam || racks[0]?.rack_code)) return;
		const code = locationParam.toUpperCase();
		setHighlightCode(code);
		requestAnimationFrame(() => {
			const el = gridRef.current?.querySelector(
				`[data-slot-code="${code}"]`,
			) as HTMLElement | null;
			el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rackDetail]);

	const gridRef = useRef<HTMLDivElement>(null);

	// ─── Find material by ID ─────────────────────────────────────
	// Resolves a material's friendly_id to its location, then jumps to that
	// rack and highlights the slot. The highlight is deferred to `pendingTarget`
	// because switching racks resets `highlightCode` (effect above) and the slot
	// element only exists once the new rack detail has rendered.
	const [materialQuery, setMaterialQuery] = useState('');
	const [locateResult, setLocateResult] = useState<ILocateMaterialResult | null>(null);
	const [pendingTarget, setPendingTarget] = useState<{
		rackCode: string;
		code: string;
	}>();
	const [locateMaterial, { isFetching: locating }] = useLazyLocateMaterialQuery();

	const handleFindMaterial = async () => {
		const id = materialQuery.trim();
		if (!id) return;
		try {
			const result = await locateMaterial(id).unwrap();
			setLocateResult(result);
			if (!result.located || !result.rack || !result.location) {
				notification.info({
					message: `Material ${result.friendly_id}`,
					description: 'This material is not assigned to any location.',
				});
				return;
			}
			const params = new URLSearchParams(searchParams);
			if (result.building) params.set('building', result.building.id);
			params.set('rack', result.rack.rack_code);
			setSearchParams(params);
			setPendingTarget({ rackCode: result.rack.rack_code, code: result.location.code });
		} catch (err: any) {
			setLocateResult(null);
			notification.error({
				message: 'Material not found',
				description: err?.data?.detail || `No material with ID "${id}".`,
			});
		}
	};

	// Apply the deferred highlight once the target rack's detail has loaded, then
	// scroll its slot into view (the cell carries a data-slot-code attribute).
	useEffect(() => {
		if (!pendingTarget || !rackDetail) return;
		if (rackDetail.rack_code !== pendingTarget.rackCode) return;
		setHighlightCode(pendingTarget.code);
		const code = pendingTarget.code.toUpperCase();
		setPendingTarget(undefined);
		requestAnimationFrame(() => {
			const el = gridRef.current?.querySelector(
				`[data-slot-code="${code}"]`,
			) as HTMLElement | null;
			el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
		});
	}, [pendingTarget, rackDetail]);

	const [deleteRack] = useDeleteRackMutation();
	const [deleteShelf] = useDeleteShelfMutation();
	const [recalculateRack, { isLoading: recalculating }] = useRecalculateRackValuesMutation();

	const handleRecalculate = async () => {
		if (!selectedRackId) return;
		try {
			const result = await recalculateRack(selectedRackId).unwrap();
			notification.success({
				message: `Recalculated ${result.recalculated} location${result.recalculated !== 1 ? 's' : ''}`,
			});
		} catch {
			notification.error({ message: 'Recalculate failed', description: 'Could not recalculate rack values.' });
		}
	};

	// ─── Modal state ─────────────────────────────────────────────
	const [rackModal, setRackModal] = useState<{ open: boolean; rack?: IRackListItem | null }>({
		open: false,
	});
	const [shelfModal, setShelfModal] = useState<{
		open: boolean;
		shelf?: IShelfWithLocations | null;
	}>({ open: false });
	const [locationModal, setLocationModal] = useState<{
		open: boolean;
		shelfId?: string;
		location?: ILocationDiagram | null;
		prefillCode?: string;
	}>({ open: false });
	const [detailModal, setDetailModal] = useState<{
		open: boolean;
		location?: ILocationDiagram | null;
		shelf?: IShelfWithLocations | null;
	}>({ open: false });

	const handleBuildingChange = (value: string) => {
		// Preserve the selected rack code across buildings: when the target
		// building has a rack with the same code, the find-by-code selection
		// keeps the user in context. The reconciliation effect below clears the
		// param if the new building has no matching rack. See PTB-5921.
		const params = new URLSearchParams(searchParams);
		params.set('building', value);
		setSearchParams(params);
	};

	const showDeleteError = (err: any, fallback: string) =>
		notification.error({
			message: 'Could not delete',
			description: err?.data?.detail || fallback,
		});

	const handleDeleteRack = async (rack: IRackListItem) => {
		if (isShopManager === false) { showPermissionDenied(); return; }
		try {
			await deleteRack(rack.id).unwrap();
			notification.success({ message: `Rack ${rack.rack_code} deleted` });
			if (selectedRackId === rack.id) selectRack(undefined);
		} catch (err) {
			showDeleteError(err, 'This rack could not be deleted.');
		}
	};

	const handleDeleteShelf = async (shelf: IShelfWithLocations) => {
		if (isShopManager === false) { showPermissionDenied(); return; }
		try {
			await deleteShelf(shelf.id).unwrap();
			notification.success({ message: `Shelf ${shelf.name} deleted` });
		} catch (err) {
			showDeleteError(err, 'This shelf could not be deleted.');
		}
	};

	// Rack actions for the collapsed (tablet) overflow dropdown. Mirrors the
	// inline buttons; the destructive action confirms via Modal.confirm because
	// a Popconfirm can't anchor reliably inside a closing dropdown menu.
	const rackActionItems: MenuProps['items'] = [
		{
			key: 'add-location',
			icon: <PlusOutlined />,
			label: 'Add Location',
			disabled: !rackDetail?.shelves.length,
			onClick: requireShopManager(() => setLocationModal({ open: true, location: null })),
		},
		{
			key: 'add-shelf',
			icon: <PlusOutlined />,
			label: 'Add Shelf',
			onClick: requireShopManager(() => setShelfModal({ open: true, shelf: null })),
		},
		{
			key: 'edit-rack',
			icon: <EditOutlined />,
			label: 'Edit Rack',
			onClick: requireShopManager(() => setRackModal({ open: true, rack: selectedRack })),
		},
		{
			key: 'recalculate',
			icon: <SyncOutlined />,
			label: 'Recalculate',
			onClick: requireShopManager(handleRecalculate),
		},
		{ type: 'divider' },
		{
			key: 'delete-rack',
			icon: <DeleteOutlined />,
			label: 'Delete Rack',
			danger: true,
			onClick: requireShopManager(() =>
				Modal.confirm({
					title: 'Delete this rack?',
					okText: 'Delete',
					okType: 'danger',
					onOk: () => selectedRack && handleDeleteRack(selectedRack),
				}),
			),
		},
	];

	const buildingSelector =
		buildings.length > 1 ? (
			<Select
				value={buildingId}
				onChange={handleBuildingChange}
				size="large"
				suffixIcon={<EnvironmentOutlined />}
				style={{ minWidth: 220 }}
				options={buildings.map((b) => ({ label: b.name, value: b.id }))}
			/>
		) : undefined;

	return (
		<BasePageLayout
			title="Manage Locations"
			subtitle="Create and organize racks, shelves and locations"
			extra={buildingSelector}
		>
			{/* Find a stored material by its friendly_id and jump to its slot. */}
			<Card size="small" style={{ marginBottom: 16 }}>
				<Space direction="vertical" style={{ width: '100%' }} size={8}>
					<Input.Search
						allowClear
						enterButton="Find"
						loading={locating}
						prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
						placeholder="Find a material by ID (e.g. AAY) to jump to its location…"
						value={materialQuery}
						onChange={(e) => {
							setMaterialQuery(e.target.value);
							if (!e.target.value) setLocateResult(null);
						}}
						onSearch={handleFindMaterial}
						style={{ maxWidth: 520 }}
					/>
					{locateResult?.located && locateResult.rack && locateResult.location && (
						<Space size={4} wrap>
							<Tag color="green">{locateResult.friendly_id}</Tag>
							<Text type="secondary">found in</Text>
							<Tag color="blue">{locateResult.rack.rack_code}</Tag>
							{locateResult.shelf && <Text>{locateResult.shelf.name}</Text>}
							<Text type="secondary">·</Text>
							<Tag>{locateResult.location.code}</Tag>
							{locateResult.building && (
								<Text type="secondary">({locateResult.building.name})</Text>
							)}
						</Space>
					)}
					{locateResult && !locateResult.located && (
						<Text type="warning">
							{locateResult.friendly_id} exists but is not assigned to any location.
						</Text>
					)}
				</Space>
			</Card>

			<div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
				{/* Master: racks list */}
				<Card
					title={
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'space-between',
							}}
						>
							<span>Racks</span>
							<Button
								type="primary"
								size="small"
								icon={<PlusOutlined />}
								disabled={!buildingId}
								onClick={requireShopManager(() => setRackModal({ open: true, rack: null }))}
							>
								Add Rack
							</Button>
						</div>
					}
					size="small"
					style={{ width: 300, flexShrink: 0 }}
				>
					{racksLoading ? (
						<Spin />
					) : racks.length === 0 ? (
						<Empty description="No racks yet" />
					) : (
						<>
							<Space.Compact block style={{ marginBottom: 8 }}>
								<Input
									allowClear
									prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
									placeholder="Search racks…"
									value={rackQuery}
									onChange={(e) => setRackQuery(e.target.value)}
								/>
							</Space.Compact>
							{typeOptions.length > 1 && (
								<Select
									allowClear
									size="small"
									placeholder="All storage types"
									value={typeFilter}
									onChange={(v) => setTypeFilter(v)}
									style={{ width: '100%', marginBottom: 8 }}
									options={typeOptions.map((t) => ({
										label: t.label,
										value: t.value,
									}))}
								/>
							)}
							{filtersActive && (
								<div
									style={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'center',
										marginBottom: 8,
									}}
								>
									<Text type="secondary" style={{ fontSize: 12 }}>
										{filteredRacks.length} of {racks.length}
									</Text>
									<Button
										type="link"
										size="small"
										icon={<ClearOutlined />}
										onClick={resetFilters}
										style={{ padding: 0, height: 'auto' }}
									>
										Clear filters
									</Button>
								</div>
							)}
							{filteredRacks.length === 0 ? (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="No racks match"
								/>
							) : (
								<List
									dataSource={filteredRacks}
									rowKey="id"
									renderItem={(rack) => (
										<List.Item
											onClick={() => selectRack(rack.rack_code)}
											style={{
												cursor: 'pointer',
												background:
													rack.id === selectedRackId ? '#e6f4ff' : undefined,
												paddingLeft: 12,
												paddingRight: 12,
												borderRadius: 6,
											}}
										>
											<List.Item.Meta
												title={
													<Space>
														<Tag color="blue">{rack.rack_code}</Tag>
														<span>{rack.rack_name}</span>
														{rack.reservations_enabled === false && (
															<Tag color="orange">Reservations off</Tag>
														)}
													</Space>
												}
												description={
													<Text type="secondary" style={{ fontSize: 12 }}>
														{STORAGE_TYPE_LABELS[rack.storage_type] ||
															rack.storage_type}{' '}
														· {rack.shelves_count} shelves ·{' '}
														{rack.locations_count} locs · {rack.total_items}{' '}
														items
													</Text>
												}
											/>
										</List.Item>
									)}
								/>
							)}
						</>
					)}
				</Card>

				{/* Detail: selected rack */}
				<div style={{ flex: 1, minWidth: 0 }}>
					{!isSelectedVisible ? (
						<Card>
							<Empty
								image={Empty.PRESENTED_IMAGE_SIMPLE}
								description="The selected rack is hidden by the active filters."
							>
								<Button
									type="link"
									icon={<ClearOutlined />}
									onClick={resetFilters}
								>
									Clear filters
								</Button>
							</Empty>
						</Card>
					) : !selectedRackId || !rackDetail ? (
						<Card>
							<Empty description="Select a rack to view and edit its layout" />
						</Card>
					) : (
						<Spin spinning={rackLoading}>
							<div
								style={{
									border: '1px solid #f0f0f0',
									borderRadius: 8,
									background: '#fff',
								}}
							>
								{/* Sticky toolbar: rack identity, actions and quick-nav. */}
								<div
									style={{
										position: 'sticky',
										top: 0,
										zIndex: 5,
										background: '#fff',
										borderBottom: '1px solid #f0f0f0',
										borderTopLeftRadius: 8,
										borderTopRightRadius: 8,
										padding: '12px 16px',
									}}
								>
									<div
										ref={toolbarRef}
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: 8,
											width: '100%',
											flexWrap: 'wrap',
										}}
									>
										<Tag color="blue">{rackDetail.rack_code}</Tag>
										<span style={{ fontWeight: 600 }}>{rackDetail.rack_name}</span>
										{rackDetail.reservations_enabled === false && (
											<Tag color="orange">Reservations off</Tag>
										)}
										<Tag>
											{STORAGE_TYPE_LABELS[rackDetail.storage_type] ||
												rackDetail.storage_type}
										</Tag>
										{rackDetail.min_size != null && (
											<Tag color="geekblue" icon={<ColumnWidthOutlined />}>
												Holds {parseFloat(String(rackDetail.min_size))}
												{rackDetail.max_size != null
													? `–${parseFloat(String(rackDetail.max_size))}`
													: '+'}{' '}
												in long
											</Tag>
										)}
										{collapseActions ? (
											<Dropdown
												trigger={['click']}
												placement="bottomRight"
												menu={{ items: rackActionItems }}
											>
												<Button
													icon={<MoreOutlined />}
													style={{ marginLeft: 'auto' }}
												>
													Actions
												</Button>
											</Dropdown>
										) : (
											<Space style={{ marginLeft: 'auto' }}>
										<Button
											icon={<PlusOutlined />}
											disabled={!rackDetail.shelves.length}
											onClick={requireShopManager(() =>
												setLocationModal({ open: true, location: null })
											)}
										>
											Add Location
										</Button>
										<Button
											icon={<PlusOutlined />}
											onClick={requireShopManager(() =>
												setShelfModal({ open: true, shelf: null })
											)}
										>
											Add Shelf
										</Button>
									<Button
										icon={<EditOutlined />}
										onClick={requireShopManager(() =>
											setRackModal({ open: true, rack: selectedRack })
										)}
									>
										Edit Rack
									</Button>
									<Button
										icon={<SyncOutlined />}
										loading={recalculating}
										onClick={requireShopManager(handleRecalculate)}
									>
										Recalculate
									</Button>
									<Popconfirm
											title="Delete this rack?"
											disabled={isShopManager === false}
											onConfirm={() =>
												selectedRack && handleDeleteRack(selectedRack)
											}
										>
											<Button
												danger
												icon={<DeleteOutlined />}
												onClick={
													isShopManager === false
														? showPermissionDenied
														: undefined
												}
											>
												Delete Rack
											</Button>
										</Popconfirm>
											</Space>
										)}
									</div>
									<RackNavBar
										rack={rackDetail}
										highlightCode={highlightCode}
										onHighlight={setHighlightCode}
										gridRef={gridRef}
									/>
								</div>
							<div ref={gridRef} style={{ padding: 16 }}>
								<RackVisual
									rack={rackDetail}
									highlightCode={highlightCode}
									canWrite={isShopManager !== false}
									onLocationClick={(location, shelf) =>
										setDetailModal({ open: true, location, shelf })
									}
									onAddLocation={(shelf, suggestedCode) =>
										setLocationModal({
											open: true,
											shelfId: shelf.id,
											location: null,
											prefillCode: suggestedCode,
										})
									}
									onEditShelf={(shelf) => setShelfModal({ open: true, shelf })}
									onDeleteShelf={handleDeleteShelf}
									onPermissionDenied={showPermissionDenied}
								/>
							</div>
							</div>
						</Spin>
					)}
				</div>
			</div>

			{/* Modals */}
			<RackFormModal
				open={rackModal.open}
				buildingId={buildingId}
				rack={rackModal.rack}
				onSaved={(rack) => selectRack(rack.rack_code)}
				onClose={() => setRackModal({ open: false })}
			/>
			<ShelfFormModal
				open={shelfModal.open}
				rackId={selectedRackId}
				shelf={shelfModal.shelf}
				onClose={() => setShelfModal({ open: false })}
			/>
			<LocationDetailModal
				open={detailModal.open}
				location={detailModal.location ?? null}
				shelf={detailModal.shelf}
				canWrite={isShopManager !== false}
				onEdit={() => {
					// Hand off to the edit form and close the detail modal so it
					// can't linger with stale data after a save or delete.
					setLocationModal({
						open: true,
						shelfId: detailModal.shelf?.id,
						location: detailModal.location,
					});
					setDetailModal({ open: false });
				}}
				onClose={() => setDetailModal({ open: false })}
				onPermissionDenied={showPermissionDenied}
			/>
			<LocationFormModal
				open={locationModal.open}
				shelfId={locationModal.shelfId}
				storageType={rackDetail?.storage_type || ''}
				shelves={rackDetail?.shelves}
				location={locationModal.location}
				prefillCode={locationModal.prefillCode}
				onClose={() => setLocationModal({ open: false })}
			/>
		</BasePageLayout>
	);
};

export default ManageInventoryPage;
